import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { useCompany } from "../context/CompanyContext";
import { Bell, Plus, Menu, LogOut, Copy, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

import CompanySwitcher from "./company/CompanySwitcher";

/**
 * Topbar — HeadwaySpot
 * - Workspace switcher
 * - Company ID sharing (NEW)
 * - Notifications
 * - User menu
 */
export default function Topbar({ onMenuClick, title = "Dashboard" }) {

  const { user, logout } = useAuth();
  const { activeCompany } = useCompany(); // ✅ CORRECT SOURCE
  const { items: notifications = [], loading: notificationsLoading = false } =
    useNotifications();

  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const menuRef = useRef(null);
  const navigate = useNavigate();

  /* ====================================================== */
  /* Derived                                                */
  /* ====================================================== */

  const companyId = activeCompany?._id;

  const unreadCount = notifications.filter(
    (n) => !(n.read || n.isRead || n.seen)
  ).length;

  /* ====================================================== */
  /* Copy Company ID                                        */
  /* ====================================================== */

  const handleCopyCompanyId = async () => {
    if (!companyId) return;

    try {
      await navigator.clipboard.writeText(companyId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore silently
    }
  };

  /* ====================================================== */
  /* Close menu on outside click / ESC                      */
  /* ====================================================== */

  useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }

    function onKey(e) {
      if (e.key === "Escape") setMenuOpen(false);
    }

    if (menuOpen) {
      document.addEventListener("mousedown", onDocClick);
      document.addEventListener("keydown", onKey);
    }

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  /* ====================================================== */
  /* UI                                                     */
  /* ====================================================== */

  return (
    <header className="h-16 flex items-center justify-between border-b bg-white px-4 lg:px-6 shadow-sm relative z-20">

      {/* ================= LEFT ================= */}

      <div className="flex items-center gap-4 min-w-0">

        {/* Sidebar Toggle */}
        <button
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-gray-100"
          onClick={onMenuClick}
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5 text-gray-700" />
        </button>

        {/* Title */}
        <h1 className="text-lg sm:text-xl font-semibold text-gray-800 truncate">
          {title}
        </h1>

        {/* Company Switcher + ID */}
        <div className="flex items-center gap-2">

          <CompanySwitcher />

          {companyId && (
            <div className="hidden md:flex items-center gap-2 px-2 py-1 border rounded-md bg-gray-50 text-xs text-gray-600">

              <span className="font-mono truncate max-w-[120px]">
                {companyId}
              </span>

              <button
                onClick={handleCopyCompanyId}
                className="hover:text-gray-900"
                title="Copy Company ID"
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>

            </div>
          )}

        </div>

      </div>

      {/* ================= RIGHT ================= */}

      <div className="flex items-center gap-3 ml-4">

        {/* New Project */}
        <button
          onClick={() => navigate("/projects/new")}
          className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-brand-saffron text-white hover:bg-brand-saffronDark transition"
        >
          <Plus className="w-4 h-4" />
          New Project
        </button>

        {/* Notifications */}
        <button
          onClick={() => navigate("/notifications")}
          className="relative inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-gray-700" />

          {!notificationsLoading && unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* USER MENU */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center border hover:ring-2 hover:ring-indigo-200 transition"
          >
            <span className="text-sm font-semibold text-gray-700">
              {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
            </span>
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white border shadow-lg rounded-md z-50 overflow-hidden">

              <div className="px-4 py-3 border-b">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || "User"}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email || ""}
                </p>
              </div>

              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/profile");
                }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
              >
                Profile Settings
              </button>

              <button
                onClick={() => {
                  setMenuOpen(false);
                  logout?.();
                }}
                className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>

            </div>
          )}
        </div>

      </div>

    </header>
  );
}